function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import { rgba } from 'polished';
import React from 'react';
import styled from 'styled-components';
import { withSpacing } from '../lib/hoc/withSpacing';
import isUndefined from '../lib/utils/isUndefined';
import { themify, tint } from '../styles/mixins';
import { InputAddon, InputIcon, InputWrapper, Label } from './Input';
/** Copied from react-select, so they're identical */

const ArrowIcon = ({
  className
}) => /*#__PURE__*/React.createElement("span", {
  className: className
}, /*#__PURE__*/React.createElement("svg", {
  height: "20",
  width: "20",
  viewBox: "0 0 20 20",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"
})));

const StyledArrowIcon = styled(ArrowIcon).withConfig({
  displayName: "NativeSelect__StyledArrowIcon",
  componentId: "sc-7v281d-0"
})(["position:absolute;top:50%;right:6px;transform:translateY(-50%);color:hsl(0,0%,80%);&:hover{color:hsl(0,0%,60%);}"]);
export const SelectWrapper = styled.div.withConfig({
  displayName: "NativeSelect__SelectWrapper",
  componentId: "sc-7v281d-1"
})(["position:relative;font-size:", ";width:100%;height:100%;display:flex;flex:1;&:active{", "{color:hsl(0,0%,40%);}}"], x => x.theme.type.scale[x.labelSize || x.fontSize + 1], StyledArrowIcon);
export const Error = styled.span.withConfig({
  displayName: "NativeSelect__Error",
  componentId: "sc-7v281d-2"
})(["float:right;color:", ";font-size:", ";"], x => x.theme.colors.red, x => x.theme.type.scale[6]);
const StyledSelect = styled.select.withConfig({
  displayName: "NativeSelect__StyledSelect",
  componentId: "sc-7v281d-3"
})(["appearance:none;display:", ";vertical-align:", ";margin:0;padding:0 1em;width:100%;height:3em;background:", ";color:", ";font-size:", ";border:1px solid ", ";border-radius:", ";line-height:1.25;&:hover{border-color:", ";}&:focus{outline:none;border-color:", ";}&:disabled{background:", ";color:", ";}&::-webkit-input-placeholder{color:", ";}&::-moz-placeholder{color:", ";}&:-moz-placeholder{color:", ";}&:-ms-input-placeholder{color:", ";}& option:disabled{color:gray;}"], x => x.$hasAddon ? 'table-cell' : 'inline-block', x => x.$hasAddon ? 'middle' : 'top', x => x.$bgColor || 'white', x => x.$color, x => x.theme.type.scale[x.$fontSize], x => x.$borderColor, x => x.$hasAddon ? '0 3px 3px 0' : '3px', x => x.theme.colors.grayDark, x => tint(0.9, x.theme.colors.green), x => x.theme.colors.grayLight, x => x.theme.colors.grayDark, x => rgba(tint(0.75, x.$color), 0.5), x => rgba(tint(0.75, x.$color), 0.5), x => rgba(tint(0.75, x.$color), 0.5), x => rgba(tint(0.75, x.$color), 0.5));

function wrapInput(children) {
  return /*#__PURE__*/React.createElement(InputWrapper, null, children);
}

const NativeSelectFunc = ({
  addonColor = 'grayLighter',
  addonContent,
  addonPadding = 1,
  addonSide = 'start',
  bgColor,
  borderColor = 'gray',
  children,
  className,
  color = 'grayBlack',
  defaultValue,
  disabled,
  error,
  fontSize = 5,
  icon,
  iconAnimation,
  iconColor = 'grayDarker',
  id,
  label,
  labelColor = 'grayDark',
  labelSize = 6,
  onChange,
  options = [],
  placeholder,
  touched,
  value
}) => {
  if (isUndefined(value)) {
    options.unshift({
      label: placeholder || label,
      value: '',
      disabled: true,
      hidden: true
    });
  }

  const hasAddon = Boolean(icon || addonContent);

  const handleChange = e => {
    const possibleNumberValue = !isNaN(parseInt(e.target.value)) ? parseInt(e.target.value) : e.target.value;
    const targetOption = options.find(o => o.value === possibleNumberValue);
    if (targetOption) return onChange(targetOption);
  };

  const selectOptions = options ? options.map((option, index) => /*#__PURE__*/React.createElement("option", _extends({
    key: `${option.value}_${index}`
  }, option), option.label)) : children;
  const AddonComponent = (addonContent || icon) && /*#__PURE__*/React.createElement(InputAddon, {
    $addonColor: themify(addonColor),
    $addonPadding: addonPadding,
    $addonSide: addonSide,
    $borderColor: themify(borderColor),
    $fontSize: fontSize,
    htmlFor: id,
    key: "selectAddon"
  }, icon ? /*#__PURE__*/React.createElement(InputIcon, {
    fill: themify(iconColor),
    icon: icon,
    iconAnimation: iconAnimation,
    solid: true,
    stroke: "none"
  }) : addonContent);
  const SelectComponent = /*#__PURE__*/React.createElement(SelectWrapper, {
    fontSize: fontSize,
    key: "selectWrapper"
  }, /*#__PURE__*/React.createElement(StyledSelect, {
    $addonSide: addonSide,
    $bgColor: themify(bgColor),
    $borderColor: themify(touched && error ? 'red' : borderColor),
    $color: themify(isUndefined(value) ? 'gray' : color),
    $fontSize: fontSize,
    $hasAddon: hasAddon,
    defaultValue: defaultValue,
    disabled: disabled,
    id: id,
    onChange: handleChange,
    placeholder: placeholder || label,
    value: (value == null ? void 0 : value.value) || '',
    key: "select"
  }, selectOptions), /*#__PURE__*/React.createElement(StyledArrowIcon, null));

  const inputWithAddon = side => {
    if (side === 'end') return wrapInput([SelectComponent, AddonComponent]);
    return wrapInput([AddonComponent, SelectComponent]);
  };

  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, label && /*#__PURE__*/React.createElement(Label, {
    htmlFor: id,
    $fontSize: fontSize,
    $labelColor: themify(labelColor),
    $labelSize: labelSize
  }, label, touched && error && /*#__PURE__*/React.createElement(Error, null, error)), hasAddon ? inputWithAddon(addonSide) : SelectComponent);
};

export const NativeSelect = withSpacing(NativeSelectFunc, {
  mb: 1
});